"use client";

import { useSuspenseQuery } from "@apollo/client";
import { useI18n } from "@wojo/localization";
import { Text } from "@wojo/ui";
import cn from "clsx";
import {
    MyGlobalRewardsSummaryDocument,
    MyGlobalRewardsSummaryQuery,
} from "generated/graphql";
import { BaseLoyaltyOverviewCard } from "../base-loyalty-overview-card";
import styles from "./GlobalRewardsOverviewCard.module.scss";

// Localization's types are strict about its keys
// Transforms membership level to a number localization accepts
const getMembershipLevelNumber = (
    loyaltyLevel: number | undefined,
): 1 | 2 | 3 | 4 | 5 | null => {
    switch (loyaltyLevel) {
        case 1:
            return 1;
        case 2:
            return 2;
        case 3:
            return 3;
        case 4:
            return 4;
        case 5:
            return 5;
        default:
            return null;
    }
};

export type GlobalRewardsOverviewCardProps = {
    showSeeDetailsLink?: boolean;
};

export const GlobalRewardsOverviewCard: React.FC<
    GlobalRewardsOverviewCardProps
> = ({ showSeeDetailsLink }) => {
    const { data } = useSuspenseQuery<MyGlobalRewardsSummaryQuery>(
        MyGlobalRewardsSummaryDocument,
    );

    const loyaltyI18n = useI18n("account").account.loyalty;
    const overviewI18n = loyaltyI18n.globalRewardsOverviewCard;

    const summary = data.myGlobalRewardsSummary;
    const membershipLevelNumber = getMembershipLevelNumber(
        summary?.membershipLevelId,
    );
    const currentLevelI18n =
        typeof membershipLevelNumber === "number"
            ? loyaltyI18n.globalRewardsLevels?.[membershipLevelNumber]
            : null;

    if (!summary || !currentLevelI18n || !overviewI18n) {
        return null;
    }

    return (
        <BaseLoyaltyOverviewCard
            badge={
                <div
                    className={cn(
                        styles.badge,
                        styles[`badge--level${summary.membershipLevelId}`],
                    )}
                >
                    <Text.Body tag="span" variant="2" emphasis="2" aria-hidden>
                        {currentLevelI18n.title.l()}
                    </Text.Body>
                </div>
            }
            content={
                <div className={styles.content}>
                    <div>
                        <Text.Body className={styles["column-title"]}>
                            {overviewI18n.levelTitle.l()}
                        </Text.Body>
                        <Text.Heading tag="div" order="4">
                            {currentLevelI18n.title.l()}
                        </Text.Heading>
                    </div>
                    <div>
                        <Text.Body className={styles["column-title"]}>
                            {overviewI18n.pointsTitle.l()}
                        </Text.Body>
                        <Text.Heading tag="div" order="4">
                            {summary.pointsActive}
                        </Text.Heading>
                    </div>
                </div>
            }
            seeDetailsLink={
                showSeeDetailsLink && overviewI18n.seeDetailsLinkUrl.l()
                    ? {
                          accessibleText:
                              overviewI18n.seeDetailsLinkAccessibleText.l(),
                          url: overviewI18n.seeDetailsLinkUrl.l(),
                      }
                    : undefined
            }
        />
    );
};
